import { supabase } from "../supabase";

export type FinanceEntryType = "expense" | "revenue";
export type FinanceEntryStatus = "active" | "voided";

export type FinanceEntry = {
  id: string;
  entryType: FinanceEntryType;
  category: string;
  label: string;
  vendor: string | null;
  amountCents: number;
  currency: string;
  occurredOn: string;
  source: string;
  notes: string | null;
  receiptReference: string | null;
  status: FinanceEntryStatus;
  createdAt: string;
  voidedAt: string | null;
  voidReason: string | null;
};

export type FinanceCurrencySummary = {
  currency: string;
  revenueCents: number;
  expenseCents: number;
  netCents: number;
  breakEvenCents: number;
  entryCount: number;
};

export type FinanceOverview = {
  isAdmin: boolean;
  currencies: FinanceCurrencySummary[];
  activeEntryCount: number;
  voidedEntryCount: number;
  updatedAt: string;
};

export type NewFinanceEntry = {
  entryType: FinanceEntryType;
  category: string;
  label: string;
  vendor?: string;
  amountCents: number;
  currency: string;
  occurredOn: string;
  notes?: string;
  receiptReference?: string;
};

export async function isFinanceAdmin(): Promise<boolean> {
  const { data, error } = await supabase.rpc("precise_plate_finance_admin_status");
  if (error) throw error;
  return data === true;
}

export async function getFinanceOverview(): Promise<FinanceOverview> {
  const { data, error } = await supabase.rpc("get_precise_plate_finance_overview");
  if (error) throw error;
  return normalizeOverview(data);
}

export async function getFinanceEntries(limit = 500): Promise<FinanceEntry[]> {
  const { data, error } = await supabase.rpc("get_precise_plate_finance_entries", { p_limit: limit });
  if (error) throw error;
  if (!Array.isArray(data)) return [];
  return data.map(normalizeEntry).filter((entry): entry is FinanceEntry => entry !== null);
}

export async function createFinanceEntry(entry: NewFinanceEntry): Promise<void> {
  const { error } = await supabase.rpc("create_precise_plate_finance_entry", {
    p_entry_type: entry.entryType,
    p_category: entry.category,
    p_label: entry.label,
    p_vendor: entry.vendor?.trim() || null,
    p_amount_cents: entry.amountCents,
    p_currency: entry.currency,
    p_occurred_on: entry.occurredOn,
    p_notes: entry.notes?.trim() || null,
    p_receipt_reference: entry.receiptReference?.trim() || null,
  });
  if (error) throw error;
}

export async function voidFinanceEntry(id: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc("void_precise_plate_finance_entry", {
    p_entry_id: id,
    p_reason: reason,
  });
  if (error) throw error;
}

function normalizeOverview(value: unknown): FinanceOverview {
  const source = isRecord(value) ? value : {};
  const currencies = Array.isArray(source.currencies)
    ? source.currencies.map((item) => {
      const summary = isRecord(item) ? item : {};
      return {
        currency: String(summary.currency ?? "CAD"),
        revenueCents: toNumber(summary.revenueCents),
        expenseCents: toNumber(summary.expenseCents),
        netCents: toNumber(summary.netCents),
        breakEvenCents: toNumber(summary.breakEvenCents),
        entryCount: toNumber(summary.entryCount),
      };
    })
    : [];
  return {
    isAdmin: source.isAdmin === true,
    currencies,
    activeEntryCount: toNumber(source.activeEntryCount),
    voidedEntryCount: toNumber(source.voidedEntryCount),
    updatedAt: String(source.updatedAt ?? ""),
  };
}

function normalizeEntry(value: unknown): FinanceEntry | null {
  if (!isRecord(value) || typeof value.id !== "string") return null;
  return {
    id: value.id,
    entryType: value.entryType === "revenue" ? "revenue" : "expense",
    category: String(value.category ?? "other"),
    label: String(value.label ?? ""),
    vendor: nullableString(value.vendor),
    amountCents: toNumber(value.amountCents),
    currency: String(value.currency ?? "CAD"),
    occurredOn: String(value.occurredOn ?? ""),
    source: String(value.source ?? "manual"),
    notes: nullableString(value.notes),
    receiptReference: nullableString(value.receiptReference),
    status: value.status === "voided" ? "voided" : "active",
    createdAt: String(value.createdAt ?? ""),
    voidedAt: nullableString(value.voidedAt),
    voidReason: nullableString(value.voidReason),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nullableString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function toNumber(value: unknown): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}
